import { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";

// Mirrors `requestChangeBranchConfig` on the API (maxLength 500). Enforced here
// too so the user is stopped at the input instead of by a 400.
const MAX_NOTE_LENGTH = 500;

interface RequestChangeBranchModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (reviewNote: string) => Promise<void> | void;
}

/**
 * บัญชีสาขา "แก้ไข" — sends a submitted report back to branch finance.
 *
 * Not a plain ConfirmModal: the reason is REQUIRED, because the whole point of
 * the send-back is telling the finance user what to fix. A whitespace-only note
 * is rejected the same way the API rejects it (trim, then require).
 */
export default function RequestChangeBranchModal({
  open,
  onClose,
  onConfirm,
}: RequestChangeBranchModalProps) {
  const [reviewNote, setReviewNote] = useState("");
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const trimmedNote = reviewNote.trim();
  const isEmpty = trimmedNote.length === 0;

  const handleClose = () => {
    if (submitting) return;
    setReviewNote("");
    setTouched(false);
    onClose();
  };

  const handleConfirm = async () => {
    setTouched(true);
    if (isEmpty) return;

    setSubmitting(true);
    try {
      await onConfirm(trimmedNote);
      setReviewNote("");
      setTouched(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>ตีกลับให้การเงินแก้ไข</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          รายงานจะถูกส่งกลับไปที่การเงินสาขา และปลดล็อกให้แก้ไขข้อมูลได้อีกครั้ง
          กรุณาระบุสิ่งที่ต้องแก้ไข
        </DialogContentText>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={3}
          label="เหตุผล / สิ่งที่ต้องแก้ไข"
          placeholder="เช่น ยอดของเสียไม่ตรงกับใบตรวจนับ"
          value={reviewNote}
          onChange={(event) => setReviewNote(event.target.value)}
          onBlur={() => setTouched(true)}
          error={touched && isEmpty}
          helperText={
            touched && isEmpty
              ? "กรุณาระบุเหตุผล"
              : `${trimmedNote.length}/${MAX_NOTE_LENGTH}`
          }
          slotProps={{ htmlInput: { maxLength: MAX_NOTE_LENGTH } }}
        />
      </DialogContent>
      <DialogActions>
        <Button disabled={submitting} onClick={handleClose}>
          ยกเลิก
        </Button>
        {/*
          Deliberately NOT disabled while the reason is empty: a dead button
          explains nothing. Pressing it surfaces "กรุณาระบุเหตุผล" under the
          field — the warning arrives after an attempt, where it is readable.
        */}
        <Button
          loading={submitting}
          variant="contained"
          color="warning"
          onClick={handleConfirm}
        >
          ตีกลับ
        </Button>
      </DialogActions>
    </Dialog>
  );
}
